import { commonUtils } from '@/helpers/commonUtils'
import styles from './SubscriptionPage.module.less'
import AIcon from '@/components/A/AIcon/AIcon'
import DefaultAvatar from '@/components/DefaultAvatar/DefaultAvatar'
import { observer } from 'mobx-react'
import { userService } from '@/services/userService'
import { Link } from 'react-router-dom'
import { PAGE_LINKS } from '@/router/pageConst'
import { api } from '@/api'
import { usePagination, useRequest } from 'alova/client'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import APagination from '@/components/A/APagination/APagination'
import { useTranslation } from 'react-i18next'
import cls from 'classnames'
import useProductType from '@/helpers/commonHooks/useProductType'
import { UsageRecordProjectCategory } from '@/api/corp/corp'

function SubscriptionPage() {
  const { t, i18n } = useTranslation()
  const { productType } = useProductType()
  const getProjectTypeLabel = (projectCategory: UsageRecordProjectCategory) => {
    switch (projectCategory) {
      case 'quick_landscape':
        return t('productSwitch.modal.cards.tvcQuick.title')
      case 'base_landscape':
        return t('productSwitch.modal.cards.tvcBase.title')
      case 'survey_landscape':
        return t('productSwitch.modal.cards.tvcSurvey.title')
      case 'quick_portrait':
        return t('productSwitch.modal.cards.portraitQuick.title')
      case 'base_portrait':
        return t('productSwitch.modal.cards.portraitBase.title')
      case 'survey_portrait':
        return t('productSwitch.modal.cards.portraitSurvey.title')
      default:
        return '-'
    }
  }
  const formatUsageCount = (usageCount: number) => {
    return usageCount > 0 ? `+${usageCount}` : `${usageCount}`
  }
  const serviceDesc = [
    t('landing.service.bottomRow1'),
    t('landing.service.bottomRow3'),
    t('landing.service.bottomRow2'),
    t('landing.service.bottomRow4'),
  ]
  const { data } = useRequest(api.corp.analysisCnt({ projectCategory: productType }))
  const {
    data: recordList,
    total,
    page,
    pageSize,
    update,
  } = usePagination((pageNo, pageSize) => api.corp.anaysisUsageRecord({
    pageNo,
    pageSize,
    projectCategory: productType,
  }), {
    initialPage: 1,
    initialData: [],
    initialPageSize: 10,
    data: (data) => data.objects,
    total: (data) => data.totalCount,
    append: false,
  })
  const handlePageChange = (page: number) => {
    update({ page })
  }
  const [used, setUsed] = useState(0)
  const [infiniteInfo, setInfiniteInfo] = useState<null | {
    infiniteContractEnd: number
    infiniteContractStart: number
  }>(null)

  useEffect(() => {
    if (data) {
      if (data.isInfinite && data.infiniteInfo) {
        setInfiniteInfo(data.infiniteInfo)
        const start = dayjs(data.infiniteInfo.infiniteContractStart * 1000)
        const end = dayjs(data.infiniteInfo.infiniteContractEnd * 1000)
        const today = dayjs()
        const total = end.diff(start, 'day')
        const used = today.diff(start, 'day')
        const _used = (total - used) / total
        /** 防止后端逻辑错误导致出现的异常展示问题 */
        setUsed(_used > 1 ? 1 : _used)
        if (_used > 1) {
          console.error('used is greater than 1', _used)
        }
      } else {
        const _used = data.corpAnalyseCount / data.totalCount
        /** 防止后端逻辑错误导致出现的异常展示问题 */
        setUsed(_used > 1 ? 1 : _used)
        if (_used > 1) {
          console.error('used is greater than 1', _used)
        }
      }
    }
  }, [data])
  return (
    <div className={styles.subscriptionPage} data-testid='subscription-page'>
      <div className={styles.subscriptionPageContent}>
        <div className={styles.planTitle}>{t('dashboard.subscriptionPage.currentPlan')}</div>
        <div className={styles.planBox}>
          <div className={cls(styles.currentPlan, styles[i18n.language])}>
            <div className={styles.invoiceBtn} style={{ display: 'none' }}>
              <span>{t('dashboard.subscriptionPage.getInvoice')}</span>
              <span className={styles.icon}>
                <AIcon type='icon-line-global_arrow' width={18} />
              </span>
            </div>
            {userService.userInfo && (
              <div className={styles.user}>
                <DefaultAvatar
                  width={40}
                  height={40}
                  avatar={userService.userInfo.avatar}
                  name={userService.userInfo.name}
                />
                <span className={styles.email}>{userService.userInfo.email}</span>
              </div>
            )}
            {
              infiniteInfo
                ? <p className={styles.remain}>
                  <span className={styles.unlimited}>
                    <AIcon type="icon-fill-global_Unlimited2" style={{ fontSize: 18, marginTop: -2 }} />
                    <span>{t('dashboard.subscriptionPage.unlimited')}</span>
                  </span>
                  <span className={styles.dueDate}>{t('dashboard.subscriptionPage.dueDate')}: {dayjs(infiniteInfo.infiniteContractEnd * 1000).format('YYYY-MM-DD')}</span>
                </p>
                : <p className={styles.remain}>
                  {t('dashboard.subscriptionPage.remainingTimes')}: {data?.corpAnalyseCount}
                </p>
            }
            <div className={styles.remainBar}>
              <div className={styles.usedBar} style={{ width: `${used * 100}%` }}></div>
            </div>
            <Link to={PAGE_LINKS.pricing} style={{ textDecoration: 'none' }} target='_blank'>
              <div className={styles.purchaseBtn} data-testid='subscription-purchase-btn'>
                <span>🚀</span>
                <span>{t('dashboard.subscriptionPage.purchaseMore')}</span>
              </div>
            </Link>
          </div>
          <div className={styles.teamPlan}>
            <Link to={PAGE_LINKS.contact} style={{ textDecoration: 'none' }} target='_blank'>
              <div className={styles.contactUs} data-testid='subscription-contact-us-btn'>
                <span className={styles.text}>{t('dashboard.subscriptionPage.contactUs')}</span>
                <AIcon type='icon-line-global_arrow' />
              </div>
            </Link>
            <div className={styles.teamPlanTitle}>
              <img src={commonUtils.getImagePath(`subscriptionPage/team-plan-icon.png`)} alt='' />
              <span>{t('dashboard.subscriptionPage.teamPlan')}</span>
            </div>
            <div className={styles.descRows}>
              {serviceDesc.map((desc, index) => {
                return (
                  <div className={styles.row} key={index}>
                    <AIcon type='icon-line-global_check' className={styles.rowIcon}></AIcon>
                    <p className={styles.rowDesc}>{desc}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className={styles.recordBox}>
          <div className={styles.recordTitle}>
            {t('dashboard.subscriptionPage.table.usageRecords')}
          </div>
          <div className={styles.recordTable}>
            <div className={styles.tableHeader}>
              <div className={styles.headerItem}>{t('dashboard.subscriptionPage.table.date')}</div>
              <div className={styles.headerItem}>
                {t('dashboard.subscriptionPage.table.projectName')}
              </div>
              <div className={styles.headerItem}>
                {t('dashboard.subscriptionPage.table.projectType')}
              </div>
              <div className={styles.headerItem}>
                {t('dashboard.subscriptionPage.table.usageCount')}
              </div>
              <div className={styles.headerItem}>
                {t('dashboard.subscriptionPage.table.userName')}
              </div>
            </div>
            <div className={styles.tableContent}>
              {recordList.length > 0 ? (
                recordList.map((record, index) => {
                  return (
                    <div className={styles.row} key={index}>
                      <div className={styles.rowItem}>
                        {dayjs(record.createdAt * 1000).format('YYYY-MM-DD HH:mm')}
                      </div>
                      <div className={styles.rowItem}>{record.projectName}</div>
                      <div className={styles.rowItem}>
                        {getProjectTypeLabel(record.projectCategory)}
                      </div>
                      <div className={styles.rowItem}>{formatUsageCount(record.usageCount)}</div>
                      <div className={styles.rowItem}>
                        <DefaultAvatar
                          style={{ marginRight: 8 }}
                          avatar={record.staffAvatar}
                          name={record.staffName}
                        />
                        <span className={styles.name}>{record.staffName}</span>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className={styles.recordEmptyBox}>
                  <img
                    src={commonUtils.getImagePath(`projectPage/project-list-empty.png`)}
                    alt=''
                  />
                  <p>{t('dashboard.subscriptionPage.table.noRelevantContent')}</p>
                </div>
              )}
            </div>
          </div>
          {total !== undefined && total > pageSize && (
            <APagination
              testId='subscription-record-pagination'
              style={{ marginTop: 16 }}
              align='end'
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default observer(SubscriptionPage)
